import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Descriptions, Tag, Empty, Tooltip, Divider } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faCircle, faPlugCircleCheck } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import {
  useLastLiftMessage,
  useLastBankMessage,
  useAllLiftMessages,
  useEverReceivedForBank,
  useBankStateSummary,
  useBankServiceEntries,
  useBitmask,
} from "../socketMessageState/BankMessagesContext";
import { BankStateSummary } from "../socketMessageState/bankStateSummary";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { extractSuffix } from "../../../utils/stateMapHelpers";
import { uuidToString, stringToUuidT, EMPTY_UUID, ZERO_UUID_STRING } from "../../../utils/uuid";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { DeviceDirection } from "../../../types/DeviceDirection";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
import { serviceColorByName } from "../../../utils/serviceModeDisplay";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import { RootState } from "../../../app/store";
import { getMessageTypeColor } from "../../../constants/iconDefinition";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import MonitoringHeartBeat from "../monitoring/MonitoringHeartBeat";
import formatUuidT from "../../../flatbuffers/display/uuid";
import useGetOneAdapter from "../../../customHooks/GET_requests/useGetOneAdapter";
import { useGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import { KeyInterface } from "../../../interfaces/Key.interface";
import {
  CdoExpectedEntry,
  extractCdoExpectedEntries,
  extractCdoEntriesByLift,
  extractCdoEntriesFromModbusTemplate,
  extractCdoEntriesFromModbusTemplateByLift,
  extractCdoEntriesFromOpcUaMapping,
  extractCdoEntriesFromOpcUaMappingByLift,
} from "./cdoExpectedKeys";
import useModbusCdoTemplate from "./useModbusCdoTemplate";
import useOpcUaCdoMapping from "./useOpcUaCdoMapping";

function getDirectionColor(direction: DeviceDirection): string {
  if (direction === Usage.Up) return "green";
  if (direction === Usage.Down) return "red";
  return "default";
}

// CDO dictionary reception state → tag color. Grey when the CDO predicted a
// key but nothing has arrived yet, green once received, red when the expected
// key explicitly reads off.
function cdoReceptionColor(value: boolean | undefined): string {
  if (value === undefined) return "default";
  return value ? "green" : "red";
}

interface CdoMessageEntry {
  key: string;
  label: string;
  value: boolean | undefined;
}

function resolveExpectedValue(
  entry: CdoExpectedEntry,
  bitmask: { [key: string]: boolean },
  currentPosition: number | null | undefined,
): boolean | undefined {
  // Traffic.P entries are tracked in positionMap (applyElevatorTrafficDelta),
  // not the bitmask — green iff the car is currently at this position. When
  // no Traffic.P has been observed yet, currentPosition is undefined/null and
  // the entry stays grey.
  if (entry.positionTarget !== undefined) {
    if (currentPosition === undefined || currentPosition === null) return undefined;
    return currentPosition === entry.positionTarget;
  }
  return entry.bitmaskKey ? bitmask[entry.bitmaskKey] : undefined;
}

function buildCdoEntries(
  everReceived: ReadonlySet<string>,
  expectedEntries: readonly CdoExpectedEntry[],
  bitmask: { [key: string]: boolean },
  bankIdStr: string,
  liftIdStr: string,
  currentPosition: number | undefined,
): CdoMessageEntry[] {
  // Empty liftIdStr → match every key for this bank (used by the lift-less
  // "Bank" card). Non-empty → scope to one bank:lift pair.
  const prefix = liftIdStr ? `${bankIdStr}:${liftIdStr}:` : `${bankIdStr}:`;
  const seen = new Set<string>();
  const entries: CdoMessageEntry[] = [];
  expectedEntries.forEach((expected) => {
    if (!expected.key.startsWith(prefix)) return;
    if (seen.has(expected.key)) return;
    seen.add(expected.key);
    entries.push({
      key: expected.key,
      label: expected.label,
      value: resolveExpectedValue(expected, bitmask, currentPosition),
    });
  });
  // Surface any bitmask key the CDO didn't predict (e.g. unusual faults).
  everReceived.forEach((key) => {
    if (seen.has(key) || !key.startsWith(prefix)) return;
    seen.add(key);
    const suffix = extractSuffix(key);
    entries.push({
      key,
      // Promote any single-colon suffix to the `::` display style used by
      // the CDO manifest above for visual consistency.
      label: suffix.replace(/:/, "::"),
      value: bitmask[key],
    });
  });
  entries.sort((a, b) => a.label.localeCompare(b.label));
  return entries;
}

function getMessageCategoryColor(category: string): string {
  const normalizedCategory = category.trim().toUpperCase();

  if (normalizedCategory === "USAGE") {
    return getMessageTypeColor(
      EncodedMessageContent.OneHotMessage,
      MessageContent.Usage_UsageMessage,
    );
  }
  if (normalizedCategory === "TRAFFIC") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      MessageContent.Traffic_TrafficMessage,
    );
  }
  if (normalizedCategory === "CONTROL") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      MessageContent.Control_ControlMessage,
    );
  }
  if (normalizedCategory === "FAULT") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      MessageContent.Fault_FaultMessage,
    );
  }
  if (normalizedCategory === "STATUS") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      MessageContent.Status_StatusMessage,
    );
  }

  return getMessageTypeColor(
    EncodedMessageContent.BitMaskMessage,
    MessageContent.Status_StatusMessage,
  );
}

const NO_CONTRIBUTORS = "\u2014";

/**
 * "1 of 3 open" — or an em dash when no connected lift is reporting, so an
 * empty bank reads as "nothing to report" rather than as a real zero.
 */
function bankCountLabel(count: number, contributorCount: number, suffix: string): string {
  if (contributorCount === 0) return NO_CONTRIBUTORS;
  return `${count} of ${contributorCount} ${suffix}`;
}

/**
 * Tag colour for a bank count — neutral while nothing is reporting.
 *
 * `idleColor` is deliberately a caller's choice rather than a fixed "red".
 * On the per-lift cards red carries the open/closed distinction for a single
 * door, where it is the only thing distinguishing the two words. On a count
 * row the colour reads as severity instead, and a bank with every door shut
 * is the ordinary resting state — colouring it red would put a second red tag
 * directly above the Faults row, where red means something is actually wrong.
 */
function bankCountColor(
  count: number,
  contributorCount: number,
  activeColor: string,
  idleColor: string,
): string {
  if (contributorCount === 0) return "default";
  return count > 0 ? activeColor : idleColor;
}

/**
 * The bank card's summary rows.
 *
 * Direction and Position are absent on purpose: a bank has no direction (its
 * cars travel opposite ways at once) and no floor. Showing either would mean
 * showing one car's value under the bank's name, which is the defect this
 * replaces. The card says so in the note below rather than leaving a silent
 * hole where a row used to be. See `bankStateSummary.ts` for the full
 * per-field rationale.
 *
 * Returned as an array of `Descriptions.Item`s rather than a nested component
 * so they stay direct children of `Descriptions`, which antd requires.
 */
function renderBankSummaryItems(summary: BankStateSummary): React.ReactElement[] {
  const { contributorCount, frontDoorsOpen, rearDoorsOpen, faultedCount, serviceModes } = summary;
  return [
    <Descriptions.Item key="frontDoors" label="Front Doors">
      <Tag
        className="generalStyles__width100px generalStyles__textCenter"
        color={bankCountColor(frontDoorsOpen, contributorCount, "green", "default")}
      >
        {bankCountLabel(frontDoorsOpen, contributorCount, "open")}
      </Tag>
    </Descriptions.Item>,
    <Descriptions.Item key="rearDoors" label="Rear Doors">
      <Tag
        className="generalStyles__width100px generalStyles__textCenter"
        color={bankCountColor(rearDoorsOpen, contributorCount, "green", "default")}
      >
        {bankCountLabel(rearDoorsOpen, contributorCount, "open")}
      </Tag>
    </Descriptions.Item>,
    <Descriptions.Item key="serviceModes" label="Service Modes">
      {serviceModes.length === 0 ? (
        <Tag className="generalStyles__width100px generalStyles__textCenter" color="default">
          {contributorCount === 0 ? NO_CONTRIBUTORS : "None"}
        </Tag>
      ) : (
        <span className="generalStyles__flex" style={{ flexWrap: "wrap", gap: 4 }}>
          {serviceModes.map((entry) => (
            <Tag
              key={entry.mode}
              className="generalStyles__textCenter"
              color={serviceColorByName(entry.mode)}
            >
              {`${entry.mode} (${entry.liftCount})`}
            </Tag>
          ))}
        </span>
      )}
    </Descriptions.Item>,
    <Descriptions.Item key="faults" label="Faults">
      <Tag
        className="generalStyles__width100px generalStyles__textCenter"
        color={bankCountColor(faultedCount, contributorCount, "red", "green")}
      >
        {bankCountLabel(faultedCount, contributorCount, "faulted")}
      </Tag>
    </Descriptions.Item>,
  ];
}

/**
 * Renders one Bitmask Map entry as a `Descriptions.Item`. Returned directly
 * (not as a nested component) so it stays a direct child of `Descriptions`,
 * which antd requires to read the item props.
 */
function renderBitmaskMapItem(key: string, value: boolean): React.ReactElement {
  const suffix = extractSuffix(key);
  const [messageType, ...subTypeParts] = suffix.split(":");
  const messageSubType = subTypeParts.join(":");
  return (
    <Descriptions.Item
      key={key}
      label={
        <span>
          <Tag
            style={{ width: "80px", textAlign: "center" }}
            color={getMessageCategoryColor(messageType)}
          >
            {messageType}
          </Tag>
          {messageSubType ? `: ${messageSubType}` : ""}
        </span>
      }
    >
      <Tooltip title={suffix} className="generalStyles__hoverCursor">
        <Tag
          className="generalStyles__width50px generalStyles__textCenter generalStyles__textEllipsis"
          color={value ? "green" : "red"}
        >
          {value ? "ON" : "OFF"}
        </Tag>
      </Tooltip>
    </Descriptions.Item>
  );
}

function StateCard({
  title,
  msg,
  color = "default",
  liftNameMap = {},
  isSingleLift = false,
  bankId = undefined,
  cdoEntries = [],
  bankSummary = undefined,
  serviceEntriesOverride = undefined,
  bitmaskOverride = undefined,
}: {
  title: string;
  msg: SocketMessageInterface;
  color?: string;
  liftNameMap?: Record<string, string>;
  isSingleLift?: boolean;
  bankId?: UuidT;
  cdoEntries?: CdoMessageEntry[];
  /** Present only on the bank card. Switches the summary block to the
   *  bank-wide aggregate and drops the rows a bank has no value for. */
  bankSummary?: BankStateSummary;
  /** Bank card only: every lift of the bank, rather than `msg`'s single
   *  entry, which is whichever car dispatched last. */
  serviceEntriesOverride?: { [key: string]: string };
  /** Bank card only: bank-level bitmask keys, rather than `msg`'s, which
   *  belong to whichever car dispatched last. */
  bitmaskOverride?: { [key: string]: boolean };
}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);

  /**
   * Which doors this bank actually has, so a door nobody configured is not
   * reported on.
   *
   * `doorStatus.rearOpen` initialises to false, so an unconfigured rear door
   * rendered as a confident "Closed" — indistinguishable from a real rear door
   * that is genuinely shut. On a bank where every floor has Rear Door OFF and
   * no rear message has ever arrived, that is a reading invented out of a
   * default. Saying nothing about a door that does not exist is the honest
   * answer.
   *
   * An EMPTY floor list means "not loaded yet", not "no doors", so both rows
   * stay in that case: hiding on ignorance would blank the panel during load
   * and on any bank whose floors failed to fetch.
   */
  const bankFloors = useSelector((state: RootState) => state.monitoring.bankSelected?.bankFloors);
  const floorsKnown = Array.isArray(bankFloors) && bankFloors.length > 0;
  const hasFrontDoor = !floorsKnown || bankFloors.some((f) => f.frontDoor);
  const hasRearDoor = !floorsKnown || bankFloors.some((f) => f.rearDoor);

  // Filter out Service:* entries — service is shown from service_map instead
  const displayMapEntries = Object.entries(bitmaskOverride ?? msg.bitmask)
    .filter(([key]) => !extractSuffix(key).startsWith("Service:"))
    .sort(([a], [b]) => a.localeCompare(b));

  // Use service_map (single value per lift, no accumulation)
  const svcKey = `${uuidToString(msg.bankId)}:${uuidToString(msg.liftId) || ""}`;
  const currentService = msg.serviceMap[svcKey] || null;

  // Use position_map for position display
  const currentPosition = msg.positionMap[svcKey] ?? msg.position;

  // Collect service_map entries — every lift of the bank on the bank card,
  // this message's own single entry on a lift card.
  const serviceEntries = Object.entries(serviceEntriesOverride ?? msg.serviceMap);

  /** Resolve a compound key (bankId:liftId) to a lift name or fallback. */
  const resolveName = (compoundKey: string): string => {
    // Extract liftId from "bankId:liftId"
    const colonIdx = compoundKey.indexOf(":");
    if (colonIdx === -1) return compoundKey;
    const id = compoundKey.substring(colonIdx + 1);
    return liftNameMap[id] || id.substring(0, 8);
  };

  return (
    <div className="monitoringView__detailsBox mb-3">
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
        }}
      >
        {/** **************************************************** Direction, Position, Doors **************************************************** */}
        <DrawerRowDivider
          icon={faPlugCircleCheck}
          title={bankSummary ? "Doors, Service, Faults" : "Direction, Position, Doors"}
        />
        <div className={` drawer__box__control__panel__${theme} mt-3`}>
          {bankSummary ? (
            <>
              <Descriptions bordered size="small" column={2}>
                {renderBankSummaryItems(bankSummary)}
              </Descriptions>
              <div className="generalStyles__infoTooltipIcon mt-2" style={{ fontSize: 12 }}>
                Counts cover the bank&apos;s connected lifts. Direction and position are per-car — a
                bank has neither, so they are shown on the lift cards below.
              </div>
            </>
          ) : (
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Direction">
                <Tag
                  className="generalStyles__width100px generalStyles__textCenter"
                  color={getDirectionColor(msg.direction)}
                >
                  {directionDisplayName(msg.direction)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Position">
                <Tag className="generalStyles__width100px generalStyles__textCenter" color="cyan">
                  {currentPosition}
                </Tag>
              </Descriptions.Item>
              {hasFrontDoor && (
                <Descriptions.Item label="Front Door">
                  <Tag
                    className="generalStyles__width100px generalStyles__textCenter"
                    color={msg.doorStatus.frontOpen ? "green" : "red"}
                  >
                    {msg.doorStatus.frontOpen ? "Open" : "Closed"}
                  </Tag>
                </Descriptions.Item>
              )}
              {hasRearDoor && (
                <Descriptions.Item label="Rear Door">
                  <Tag
                    className="generalStyles__width100px generalStyles__textCenter"
                    color={msg.doorStatus.rearOpen ? "green" : "red"}
                  >
                    {msg.doorStatus.rearOpen ? "Open" : "Closed"}
                  </Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Service Mode">
                <Tag
                  className="generalStyles__width100px generalStyles__textCenter"
                  color={currentService ? serviceColorByName(currentService) : "default"}
                >
                  {currentService ?? "None"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Faults">
                <Tag
                  className="generalStyles__width100px generalStyles__textCenter"
                  color={msg.faults ? "red" : "green"}
                >
                  {msg.faults ? "Active" : "None"}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          )}
        </div>
        <br />
        {/** **************************************************** CONNECTION Map **************************************************** */}
        <DrawerRowDivider icon={faPlugCircleCheck} title="Connection Map" />
        <div className={` drawer__box__control__panel__${theme} mt-3`}>
          <div className="generalStyles__flex generalStyles__width100">
            <div className="generalStyles__width100">
              <MonitoringHeartBeat bankId={formatUuidT(bankId) ?? ""} type="DESCRIPTION" />
            </div>
            <div className="generalStyles__width50px generalStyles__flexCenter">
              <Tooltip
                overlayInnerStyle={{ width: 450, maxWidth: 450, padding: 10 }}
                title={
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <div
                        className="generalStyles__flexCenter"
                        style={{ justifyContent: "flex-start", gap: 8 }}
                      >
                        <FontAwesomeIcon icon={faCircle} className="generalStyles__success" />
                        <span className="generalStyles__width150px">
                          {t("general.good")}:{" "}
                        </span>{" "}
                        <span>
                          healthy network connectivity and healthy communication to a controller or
                          third party interface
                        </span>
                      </div>
                    </div>
                    <Divider />
                    <div style={{ marginBottom: 8 }}>
                      <div
                        className="generalStyles__flexCenter"
                        style={{ justifyContent: "flex-start", gap: 8 }}
                      >
                        <FontAwesomeIcon icon={faCircle} className="generalStyles__error" />
                        <span className="generalStyles__width150px">
                          {t("general.noNetwork")}:{" "}
                        </span>{" "}
                        <span>no TCP or UDP connectivity</span>
                      </div>
                    </div>
                    <Divider />
                    <div>
                      <div
                        className="generalStyles__flexCenter"
                        style={{ justifyContent: "flex-start", gap: 8 }}
                      >
                        <FontAwesomeIcon icon={faCircle} className="generalStyles__warning" />
                        <span className="generalStyles__width150px">
                          {t("general.noComm")}:{" "}
                        </span>{" "}
                        <span>
                          network connectivity is healthy but no communication with a controller has
                          been achieved
                        </span>
                      </div>
                    </div>
                  </div>
                }
              >
                <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__infoTooltipIcon" />
              </Tooltip>
            </div>
          </div>
        </div>
        <br />

        {/** **************************************************** Service Map **************************************************** */}
        <DrawerRowDivider icon={faPlugCircleCheck} title="Service Map" />
        <div className={` drawer__box__control__panel__${theme} mt-3`}>
          {serviceEntries.length > 0 ? (
            <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
              {serviceEntries.map(([key, val]) => (
                <Descriptions.Item key={key} label={isSingleLift ? "Service" : resolveName(key)}>
                  <Tag
                    className="generalStyles__width100px generalStyles__textCenter"
                    color={serviceColorByName(val)}
                  >
                    {val}
                  </Tag>
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : (
            <Empty description="No service entries" style={{ marginTop: 12 }} />
          )}
        </div>
        <br />
        {/** **************************************************** Bitmask Map **************************************************** */}
        <DrawerRowDivider icon={faPlugCircleCheck} title="Bitmask Map" />
        <div className={` drawer__box__control__panel__${theme} mt-3`}>
          {displayMapEntries.length === 0 ? (
            <Empty
              description={bitmaskOverride ? "No bank-level bitmask entries" : "No bitmask entries"}
              style={{ marginTop: 12 }}
            />
          ) : (
            <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
              {displayMapEntries.map(([key, value]) => renderBitmaskMapItem(key, value))}
            </Descriptions>
          )}
        </div>
        <br />
        {/** **************************************************** CDO Messages **************************************************** */}
        <DrawerRowDivider icon={faPlugCircleCheck} title="CDO Messages" />
        <div className={` drawer__box__control__panel__${theme} mt-3`}>
          {cdoEntries.length === 0 ? (
            <Empty description="No CDO messages" style={{ marginTop: 12 }} />
          ) : (
            <div
              className="generalStyles__flex"
              style={{ flexWrap: "wrap", gap: 4, marginTop: 12 }}
            >
              {cdoEntries.map((entry) => (
                <Tooltip key={entry.key} title={entry.label} className="generalStyles__hoverCursor">
                  <Tag
                    className="generalStyles__textCenter generalStyles__textEllipsis"
                    style={{ width: "120px" }}
                    color={cdoReceptionColor(entry.value)}
                  >
                    {entry.label}
                  </Tag>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
        <br />
      </div>
    </div>
  );
}

function ControlPanelStateView({
  liftId = "",
  bankId = "",
  bankLifts = [],
  adapterId = "",
}: {
  liftId?: string;
  bankId?: string;
  bankLifts?: LiftInterface[];
  /** When provided, the adapter's CDO is read to seed grey "expected but
   *  not-yet-received" message blocks. Omit to keep the legacy behaviour
   *  (e.g. in replay mode where the live adapter isn't the source of truth). */
  adapterId?: string;
}) {
  const liftUuid = useMemo(() => (liftId ? stringToUuidT(liftId) : EMPTY_UUID), [liftId]);
  const bankUuid = useMemo(() => (bankId ? stringToUuidT(bankId) : EMPTY_UUID), [bankId]);
  const liftMsg: SocketMessageInterface | undefined = useLastLiftMessage(liftUuid);
  const bankMsg: SocketMessageInterface | undefined = useLastBankMessage(bankUuid);
  const allLiftMessages = useAllLiftMessages();
  const bankEverReceived = useEverReceivedForBank(bankUuid);
  // Honest bank-wide aggregate, derived from every connected lift of the bank
  // rather than from whichever car dispatched most recently.
  const bankSummary = useBankStateSummary(bankUuid);
  const bankServiceEntries = useBankServiceEntries(bankUuid);
  // The merged, never-clobbered bitmask. `byBank[bk].bitmask` cannot be used
  // for the bank card: the reducer overwrites that entry with each lift
  // message, so bank-level keys survive there only until the next car
  // reports. The merged map keeps every key it has ever seen.
  const mergedBitmask = useBitmask();
  const { adapter } = useGetOneAdapter({ id: adapterId || "--" });
  // Modbus CDO can't be parsed from cdoTemplateJson — fetch + unpack the
  // /adapters/{id}/cdo binary so its dictionary joins the other protocols'.
  const modbusTemplate = useModbusCdoTemplate(adapterId, adapter.protocolType);
  // OPC UA CDO is union-heavy and likewise can't be parsed from JSON — fetch
  // + decode the /adapters/{id}/cdo binary so its dictionary joins the rest.
  const opcUaMapping = useOpcUaCdoMapping(adapterId, adapter.protocolType);

  // Adapter Key records (Archive.Key) are the FlatBuffer-typed source of truth
  // for LN1/RVU/CGA/LobbyVision CDO mappings, so we read them here in addition
  // to the embedded JSON walkers used by Sequent/BACnet/OPC.
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);
  const { data: adapterKeysData } = useGetAdapterKeysQuery(
    { adapterId, token },
    { skip: !token || !adapterId },
  );
  const adapterKeys: readonly KeyInterface[] = useMemo(
    () => (adapterKeysData?.data as KeyInterface[] | undefined) ?? [],
    [adapterKeysData],
  );

  const socketMsg: SocketMessageInterface = (liftId ? liftMsg : bankMsg) || BLANK_SOCKET_MESSAGE;

  // Build a name lookup from bankLifts for contextual labels
  const liftNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    bankLifts.forEach((lift) => {
      const liftKey = uuidToString(lift.id);
      const liftName = typeof lift.name === "string" ? lift.name : "";
      if (liftKey !== ZERO_UUID_STRING && liftName) {
        map[liftKey] = liftName;
      }
    });
    return map;
  }, [bankLifts, adapterId]);

  const liftCards = useMemo(() => {
    if (!bankId || bankLifts.length === 0) return [];
    return bankLifts
      .map((lift) => ({
        lift,
        liftKey: uuidToString(lift.id),
        msg:
          uuidToString(lift.id) === ZERO_UUID_STRING
            ? null
            : allLiftMessages[uuidToString(lift.id)] || null,
      }))
      .filter((entry) => entry.msg !== null);
  }, [bankId, bankLifts, allLiftMessages]);

  const isBankView = !!bankId && bankLifts.length > 0;

  // Use the route-supplied bankId/liftId rather than socketMsg.bankId since
  // socketMsg may still be BLANK_SOCKET_MESSAGE before any frame arrives.
  const soloExpectedEntries = useMemo(
    () => [
      ...extractCdoExpectedEntries(adapter, bankId, liftId, adapterKeys),
      ...extractCdoEntriesFromModbusTemplate(modbusTemplate, bankId, liftId),
      ...extractCdoEntriesFromOpcUaMapping(opcUaMapping, bankId, liftId),
    ],
    [adapter, bankId, liftId, adapterKeys, modbusTemplate, opcUaMapping],
  );
  // positionMap is undefined when no Traffic.P has arrived → grey blocks.
  const soloPosition = socketMsg.positionMap[`${bankId}:${liftId}`];
  const soloCdoEntries = useMemo(
    () =>
      buildCdoEntries(
        bankEverReceived,
        soloExpectedEntries,
        socketMsg.bitmask,
        bankId,
        liftId,
        soloPosition,
      ),
    [bankEverReceived, soloExpectedEntries, socketMsg.bitmask, bankId, liftId, soloPosition],
  );

  // Bank-level CDO messages use bank_id == lift_id in their bitmask key prefix.
  const bankExpectedEntries = useMemo(
    () => [
      ...extractCdoExpectedEntries(adapter, bankId, bankId, adapterKeys),
      ...extractCdoEntriesFromModbusTemplate(modbusTemplate, bankId, bankId),
      ...extractCdoEntriesFromOpcUaMapping(opcUaMapping, bankId, bankId),
    ],
    [adapter, bankId, adapterKeys, modbusTemplate, opcUaMapping],
  );
  // Bank-level bitmask keys only (`bankId:bankId:`). Unioning every lift's
  // keys here instead would render duplicate, unlabelled rows — the display
  // strips each key down to its `Type:Sub` suffix, so three cars reporting
  // the same signal become three identical rows with no way to tell them
  // apart. The per-lift cards already show each car's own keys, and this
  // scoping matches what the CDO Messages block below already does.
  const bankLevelBitmask = useMemo(() => {
    const prefix = `${bankId}:${bankId}:`;
    const scoped: { [key: string]: boolean } = {};
    Object.entries(mergedBitmask).forEach(([key, value]) => {
      if (key.startsWith(prefix)) scoped[key] = value;
    });
    return scoped;
  }, [mergedBitmask, bankId]);

  const bankPosition = socketMsg.positionMap[`${bankId}:${bankId}`];
  const bankAggregateCdoEntries = useMemo(
    () =>
      buildCdoEntries(
        bankEverReceived,
        bankExpectedEntries,
        socketMsg.bitmask,
        bankId,
        bankId,
        bankPosition,
      ),
    [bankEverReceived, bankExpectedEntries, socketMsg.bitmask, bankId, bankPosition],
  );

  // Single bank-wide walk → lookup-by-lift. Saves N+1 re-parses of cdoInputJson
  // and N+1 full discrete_2_message iterations on each render. Adapter Keys
  // are scope-less, so they're applied to every lift bucket we'll render.
  const liftIdsForKeys = useMemo(
    () => bankLifts.map((l) => uuidToString(l.id)).filter((id) => id !== ZERO_UUID_STRING),
    [bankLifts],
  );
  const expectedByLift = useMemo(
    () => extractCdoEntriesByLift(adapter, bankId, adapterKeys, liftIdsForKeys),
    [adapter, bankId, adapterKeys, liftIdsForKeys],
  );
  // Walk the Modbus template once per (template, bank, lifts) rather than once
  // per lift on every 5Hz monitoring re-render of liftCardCdoEntries below.
  const modbusEntriesByLift = useMemo(
    () => extractCdoEntriesFromModbusTemplateByLift(modbusTemplate, bankId, liftIdsForKeys),
    [modbusTemplate, bankId, liftIdsForKeys],
  );
  // Walk the OPC UA mapping once per (mapping, bank, lifts) for the same reason.
  const opcUaEntriesByLift = useMemo(
    () => extractCdoEntriesFromOpcUaMappingByLift(opcUaMapping, bankId, liftIdsForKeys),
    [opcUaMapping, bankId, liftIdsForKeys],
  );
  const liftCardCdoEntries = useMemo(
    () =>
      liftCards.map((entry) => {
        const entryMsg = entry.msg as SocketMessageInterface;
        const entryLiftIdStr = entry.lift.id
          ? uuidToString(entry.lift.id)
          : uuidToString(entryMsg.liftId);
        const expected = [
          ...(expectedByLift.get(entryLiftIdStr) ?? []),
          ...(modbusEntriesByLift.get(entryLiftIdStr) ?? []),
          ...(opcUaEntriesByLift.get(entryLiftIdStr) ?? []),
        ];
        const entryPos = entryMsg.positionMap[`${bankId}:${entryLiftIdStr}`];
        return buildCdoEntries(
          bankEverReceived,
          expected,
          entryMsg.bitmask,
          bankId,
          entryLiftIdStr,
          entryPos,
        );
      }),
    [liftCards, bankEverReceived, bankId, expectedByLift, modbusEntriesByLift, opcUaEntriesByLift],
  );

  // A bank-scoped card is bank-scoped whether or not the caller supplied the
  // lift list — `bankLifts` only decides whether the per-lift cards render
  // below, never what "Bank" is allowed to claim about itself.
  if (!isBankView) {
    return (
      <StateCard
        title={liftId ? "Lift" : "Bank"}
        msg={socketMsg}
        color={liftId ? "purple" : "blue"}
        isSingleLift={!!liftId}
        bankId={bankUuid}
        cdoEntries={soloCdoEntries}
        bankSummary={liftId ? undefined : bankSummary}
        serviceEntriesOverride={liftId ? undefined : bankServiceEntries}
        bitmaskOverride={liftId ? undefined : bankLevelBitmask}
      />
    );
  }

  return (
    <Row>
      <Col xs={24} md={24}>
        <StateCard
          title="Bank"
          msg={socketMsg}
          color="blue"
          liftNameMap={liftNameMap}
          bankId={bankUuid}
          cdoEntries={bankAggregateCdoEntries}
          bankSummary={bankSummary}
          serviceEntriesOverride={bankServiceEntries}
          bitmaskOverride={bankLevelBitmask}
        />
      </Col>
      {liftCards.map((entry, idx) => (
        <Col key={entry.liftKey} xs={24} md={24}>
          <StateCard
            title={
              typeof entry.lift.name === "string" && entry.lift.name
                ? entry.lift.name
                : entry.liftKey.substring(0, 8)
            }
            msg={entry.msg as SocketMessageInterface}
            color="purple"
            liftNameMap={liftNameMap}
            isSingleLift
            cdoEntries={liftCardCdoEntries[idx]}
          />
        </Col>
      ))}
    </Row>
  );
}

export default ControlPanelStateView;
